'use client'
import { getLiveAndSummaryData } from '@/app/libs/match'
import {
  MatchLiveSummaryData,
  UpcomingMatchCard as UpcomingMatchCardProps
} from '@/app/libs/match/types'
import { GET_MATCHES_DATA } from '@/graphql/match/match.query'
import usePolling from '@/shared/hooks/usePolling'
import { BatIcon } from '@/shared/Icons'
import { getSlugUrl } from '@/shared/utils'
import { useQuery } from '@apollo/client'
import { useState } from 'react'
import CustomLink from '../../customLink'
import ThemeTable from '../../themeTable'
import MatchProbability from '../../widgets/probability'
import { useMatchContext } from '../matchContext'
import SeriesTitle from '../matchDetailOverview/seriesTitle'
import InningsProgression from './innings-progression'
import NewsList from './news-list'
import OverRibbon from './over-ribbon'
import Review from './review'
import SessionTable from './sessionTable'
import TopPerformers from './top-performers'
import UpcomingMatches from './upcoming-matches'

type Seo = {
  sSlug: string
}

type LiveInningPlayer = {
  isCaptain: boolean
  isWk: boolean
  bImpactPlayer: boolean
  sFullName: string
  sShortName?: string
  sTitle?: string | null
  eTagStatus?: string
  oSeo?: Seo
}

type ScorecardBatter = {
  oBatter: LiveInningPlayer
  nSixes: number
  nRuns: number
  nFours: number
  nBallFaced: number
  sStrikeRate: string
  iBatterId: string
}

type ScorecardBowler = {
  oBowler: LiveInningPlayer
  sEcon: string
  sOvers: string
  nWickets: number
  nRunsConceded: number
  nMaidens: number
  iBowlerId: string
}

export default function LiveAndSummary({ data }: { data: MatchLiveSummaryData }) {
  const { matchDetail } = useMatchContext()

  const isSummary = matchDetail?.oStatus?.sValue !== 'live'
  const [liveData, setLiveData] = useState<MatchLiveSummaryData>(data)

  usePolling(
    () => {
      getLiveAndSummaryData({ iMatchId: matchDetail?._id }).then(data => setLiveData(data))
    },
    {
      interval: 15000,
      deps: [matchDetail?._id],
      skip: !matchDetail?._id || matchDetail?.oStatus?.sValue !== 'live'
    }
  )

  const teamA = matchDetail?.oTeamScoreA?.oTeam
  const teamB = matchDetail?.oTeamScoreB?.oTeam

  const news = liveData?.newsData?.aResults

  const [upcomingMatchData, setUpcomingMatchData] = useState<UpcomingMatchCardProps[]>([])

  useQuery(GET_MATCHES_DATA, {
    variables: { input: { eStatus: 'u', aSeriesId: matchDetail?.oSeries?._id, nLimit: 4 } },
    onCompleted: data => {
      if (data?.listAllFixturesMobileV2) {
        setUpcomingMatchData(data?.listAllFixturesMobileV2)
      }
    }
  })

  const battingReviewData = matchDetail?.oReview?.aBattingReview
  const bowlingReviewData = matchDetail?.oReview?.aBowlingReview

  const topPerformers = matchDetail.aTopPerformers

  const battingTeam = battingReviewData?.iTeamId === teamA._id ? teamA : teamB
  const bowlingTeam = bowlingReviewData?.iTeamId === teamB._id ? teamB : teamA

  const sessionData = liveData?.sessionData?.length ? liveData?.sessionData?.toReversed() : []

  const inningsProgression = matchDetail?.oSummary
  const isInningsProgression = inningsProgression?.some(inning => inning?.aSummaryGraph?.length)

  const reviewTable1Label = [
    battingTeam?.oSeo?.eStatus === 'a' ? (
      <CustomLink href={battingTeam?.oSeo?.sSlug}>{battingTeam?.sTitle}</CustomLink>
    ) : (
      <span key="team-2">{battingTeam?.sTitle}</span>
    ),
    ''
  ]
  const reviewTable2Label = [
    bowlingTeam?.oSeo?.eStatus === 'a' ? (
      <CustomLink href={bowlingTeam?.oSeo?.sSlug}>{bowlingTeam?.sTitle}</CustomLink>
    ) : (
      <span key="team-2">{bowlingTeam?.sTitle}</span>
    ),
    ''
  ]

  const aActiveBatters = liveData?.fullScoreBoardData?.at(-1)?.aActiveBatters || []
  const aActiveBowlers = liveData?.fullScoreBoardData?.at(-1)?.aActiveBowlers || []

  const labels = [
    'Batter',
    <>
      <span className="hidden sm:inline" key={'runs'}>
        Runs
      </span>
      <span className="sm:hidden" key={'r'}>
        R
      </span>
    </>,
    <>
      <span className="hidden sm:inline" key={'balls'}>
        Balls
      </span>
      <span className="sm:hidden" key={'b'}>
        B
      </span>
    </>,
    'SR',
    '4s',
    '6s'
  ]
  const bowlerLabels = ['Bowler', 'Ov', 'M', 'R', 'W', 'NB', 'WD', 'Eco']

  const table = {
    nRuns: 'Runs',
    nBallFaced: 'Balls',
    sStrikeRate: 'SR',
    nFours: '4s',
    nSixes: '6s'
  }
  const bowlerTable = {
    sOvers: 'Ov',
    nMaidens: 'M',
    nRunsConceded: 'R',
    nWickets: 'W',
    nNoBalls: 'NB',
    nWides: 'WD',
    sEcon: 'Eco'
  }

  return (
    <div className="mb-4">
      {/* over ribbon */}
      <OverRibbon matchDetail={matchDetail} />

      {!isSummary ? (
        <div className="md:hidden mb-3 mt-4">
          <MatchProbability data={matchDetail} />
        </div>
      ) : null}

      {/* series title */}
      {isSummary ? <SeriesTitle matchDetail={matchDetail} /> : null}

      {/* Top performers */}
      {isSummary && topPerformers?.length ? <TopPerformers topPerformers={topPerformers} /> : null}

      {/* active bowler and batsman */}
      {!isSummary ? (
        <>
          {aActiveBatters?.length ? (
            <ThemeTable
              key="active batter"
              labels={labels}
              className="!rounded-none md:!rounded-xl"
              isBordered
            >
              {aActiveBatters?.map((batter, index: number) => {
                const player = batter?.oBatter
                const sShortName =
                  `${player?.sFullName?.split(' ')?.[0]?.[0] || ''} ${player?.sFullName?.split(' ')?.[1]}` ||
                  ''
                const playerSlug = player?.oSeo?.sSlug
                const playerName = (
                  <>
                    <span className="hidden md:inline">
                      {player?.sFullName || player?.sShortName}
                    </span>
                    <span className="md:hidden">{sShortName}</span>
                    {player?.isCaptain && <span className="ps-1">(c)</span>}
                    {player?.isWk && <span className="ps-1">(wk)</span>}
                    {player?.bImpactPlayer && (
                      <span className="px-0.5 bg-green-600 text-[10px] rounded-sm text-white leading-4">
                        IMP
                      </span>
                    )}
                  </>
                )
                return (
                  <tr key={player?.sFullName}>
                    <td className="!w-full max-w-14" width={90}>
                      <span className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <span className="flex items-center">
                          {batter?.oBatter?.eTagStatus === 'a' && playerSlug ? (
                            <CustomLink
                              href={getSlugUrl(`/${playerSlug}`)}
                              prefetch={false}
                              className="flex items-center gap-2 w-fit"
                            >
                              {playerName}
                            </CustomLink>
                          ) : (
                            <span className="flex items-center gap-2 w-fit">{playerName}</span>
                          )}
                          {index === 0 ? (
                            <span className="ml-2">
                              <BatIcon />
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </td>
                    {Object.keys(table)?.map((value, index) => {
                      return (
                        <td
                          className={`align-top md:!px-6 !text-right ${index === 0 ? '!text-textOffWhite' : '!text-textDselected'} `}
                          key={index}
                        >
                          {batter[value as keyof Omit<ScorecardBatter, 'oBatter'>] ?? '-'}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </ThemeTable>
          ) : null}
          {aActiveBowlers?.length ? (
            <ThemeTable
              key="active bowler"
              labels={bowlerLabels}
              className="!rounded-none md:!rounded-xl"
              isBordered
            >
              {aActiveBowlers?.map((bowler, index: number) => {
                if (index !== 0) return null
                const player = bowler?.oBowler
                const sShortName =
                  `${player?.sFullName?.split(' ')?.[0]?.[0] || ''} ${player?.sFullName?.split(' ')?.[1]}` ||
                  ''
                const playerSlug = player?.oSeo?.sSlug
                const playerName = (
                  <>
                    <span className="hidden md:inline">{player?.sFullName}</span>
                    <span className="md:hidden">{sShortName}</span>
                    {player?.isCaptain && <span className="ps-1">(c)</span>}
                    {player?.isWk && <span className="ps-1">(wk)</span>}
                    {player?.bImpactPlayer && (
                      <span className="px-0.5 bg-green-600 text-[10px] rounded-sm text-white leading-4">
                        IMP
                      </span>
                    )}
                  </>
                )
                return (
                  <tr key={player?.sFullName}>
                    <td className="!w-full max-w-14" width={90}>
                      <span className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <span className="flex items-center">
                          {bowler?.oBowler?.eTagStatus === 'a' ? (
                            <CustomLink
                              href={getSlugUrl(`/${playerSlug}`)}
                              prefetch={false}
                              className="flex items-center gap-2 w-fit"
                            >
                              {playerName}
                            </CustomLink>
                          ) : (
                            <span className="flex items-center gap-2 w-fit">{playerName}</span>
                          )}
                        </span>
                      </span>
                    </td>
                    {Object.keys(bowlerTable)?.map((value, index) => {
                      return (
                        <td
                          className={`align-top md:!px-6 !text-right ${index === 0 ? '!text-textOffWhite' : '!text-textDselected'} `}
                          key={index}
                        >
                          {bowler[value as keyof Omit<ScorecardBowler, 'oBowler'>] ?? '-'}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </ThemeTable>
          ) : null}
        </>
      ) : null}

      {sessionData?.length ? (
        <SessionTable matchDetail={matchDetail} sessionData={sessionData} />
      ) : null}

      {!isSummary && (battingReviewData || bowlingReviewData) ? (
        <Review
          battingReviewData={battingReviewData}
          bowlingReviewData={bowlingReviewData}
          reviewTable1Label={reviewTable1Label}
          reviewTable2Label={reviewTable2Label}
        />
      ) : null}

      {isInningsProgression ? <InningsProgression inningsProgression={inningsProgression} /> : null}

      {upcomingMatchData?.length ? <UpcomingMatches upcomingMatchData={upcomingMatchData} /> : null}

      {news?.length ? <NewsList news={news} /> : null}
    </div>
  )
}
